import React from 'react';
import { formatAppDateTime } from '../../services/appConfigRuntime';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ServiceDetailsModal = ({ open, onClose, serviceDetails }) => {
  if (!serviceDetails) return null;

  const { coreServiceInfo, resourceUtilization, statistics, recentOperations } = serviceDetails;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">🔧 Service Details</Typography>
          <Button onClick={onClose} startIcon={<CloseIcon />}>
            Close
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Core Service Information */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                🖥️ Core Service Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Service Status:</Typography>
                  <Chip 
                    label={coreServiceInfo.serviceStatus} 
                    color={coreServiceInfo.serviceStatus === 'Running' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Service Name:</Typography>
                  <Typography variant="body2">{coreServiceInfo.serviceName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Hostname:</Typography>
                  <Typography variant="body2">{coreServiceInfo.hostname}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">IP Address:</Typography>
                  <Typography variant="body2">{coreServiceInfo.ipAddress}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Process ID:</Typography>
                  <Typography variant="body2">{coreServiceInfo.processId}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Uptime:</Typography>
                  <Typography variant="body2">{coreServiceInfo.uptimeFormatted}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Resource Utilization */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                📊 Resource Utilization
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Memory Used:</Typography>
                  <Typography variant="body2">{resourceUtilization.memoryUsedMB} MB</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Memory Total:</Typography>
                  <Typography variant="body2">{resourceUtilization.memoryTotalMB} MB</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Memory Usage:</Typography>
                  <Typography variant="body2">{resourceUtilization.memoryUsagePercent}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Active Threads:</Typography>
                  <Typography variant="body2">{resourceUtilization.activeThreads}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Max Threads:</Typography>
                  <Typography variant="body2">{resourceUtilization.maxThreads}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Queue Size:</Typography>
                  <Typography variant="body2">{resourceUtilization.queueSize}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">CPU Cores:</Typography>
                  <Typography variant="body2">{resourceUtilization.cpuCores}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Service Statistics */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                📈 Service Statistics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Number of Services:</Typography>
                  <Typography variant="body2">{statistics.numberOfServices}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Number of Servers:</Typography>
                  <Typography variant="body2">{statistics.numberOfServers}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Tasks Executed (24h):</Typography>
                  <Typography variant="body2">{statistics.tasksExecutedLast24h}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Scheduled Tasks:</Typography>
                  <Typography variant="body2">{statistics.scheduledTasks}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Running Tasks:</Typography>
                  <Typography variant="body2">{statistics.runningTasks}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Operations */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                📝 Recent Operations
              </Typography>
              <TableContainer sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Operation</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentOperations.slice(0, 5).map((op, index) => (
                      <TableRow key={index}>
                        <TableCell>{op.operation_type}</TableCell>
                        <TableCell>
                          <Chip 
                            label={op.status} 
                            color={op.status === 'SUCCESS' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {formatAppDateTime(op.timestamp)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Next Scheduled Runs */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                ⏰ Next Scheduled Runs
              </Typography>
              <TableContainer sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Flow ID</TableCell>
                      <TableCell>Trigger Type</TableCell>
                      <TableCell>Scheduled Time</TableCell>
                      <TableCell>Time Until Run</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.nextScheduledRuns && statistics.nextScheduledRuns.length > 0 ? (
                      statistics.nextScheduledRuns.map((run, index) => {
                        const scheduledTime = new Date(run.srs_start_date_time);
                        const now = new Date();
                        const timeUntilRun = scheduledTime.getTime() - now.getTime();
                        const minutesUntil = Math.floor(timeUntilRun / (1000 * 60));
                        const hoursUntil = Math.floor(minutesUntil / 60);
                        const daysUntil = Math.floor(hoursUntil / 24);
                        
                        let timeUntilText = '';
                        if (daysUntil > 0) {
                          timeUntilText = `${daysUntil}d ${hoursUntil % 24}h`;
                        } else if (hoursUntil > 0) {
                          timeUntilText = `${hoursUntil}h ${minutesUntil % 60}m`;
                        } else if (minutesUntil > 0) {
                          timeUntilText = `${minutesUntil}m`;
                        } else {
                          timeUntilText = 'Overdue';
                        }
                        
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {run.sfia_mf_id ? run.sfia_mf_id.substring(0, 8) + '...' : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={run.sfia_trigger_type || 'Unknown'} 
                                size="small"
                                color={run.sfia_trigger_type === 'Time' ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                            <TableCell>
                              {formatAppDateTime(scheduledTime)}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color={timeUntilRun < 0 ? 'error.main' : timeUntilRun < 300000 ? 'warning.main' : 'text.primary'}
                              >
                                {timeUntilText}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No scheduled runs
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceDetailsModal;
